/**
 * GoCardless Settings Component
 *
 * Admin configuration for GoCardless integration.
 * Allows setting up API credentials and webhook configuration.
 */

import React, { useState } from 'react';
import {
  Building2,
  Settings,
  Check,
  X,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import {
  useGoCardlessConfig,
  useConfigureGoCardless,
  useDisableGoCardless,
} from '../hooks/usePaymentMandates';
import { confirm } from '@/stores/confirmStore';

export function GoCardlessSettings() {
  const [isEditing, setIsEditing] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [formData, setFormData] = useState({
    accessToken: '',
    environment: 'sandbox' as 'sandbox' | 'live',
    webhookSecret: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: config, isLoading } = useGoCardlessConfig();
  const configure = useConfigureGoCardless();
  const disable = useDisableGoCardless();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.accessToken) {
      setError('Le token d\'acces est requis');
      return;
    }

    try {
      await configure.mutateAsync({
        accessToken: formData.accessToken,
        environment: formData.environment,
        webhookSecret: formData.webhookSecret || undefined,
      });

      setIsEditing(false);
      setFormData({ accessToken: '', environment: 'sandbox', webhookSecret: '' });
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la configuration');
    }
  };

  const handleDisable = async () => {
    const confirmed = await confirm({
      title: 'Desactiver GoCardless',
      message: 'Voulez-vous vraiment desactiver GoCardless ?',
      variant: 'danger',
      confirmText: 'Desactiver',
      cancelText: 'Annuler',
    });
    if (!confirmed) {
      return;
    }

    try {
      await disable.mutateAsync();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la désactivation');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-teal-50/50 to-white border-b border-white/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Building2 className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">GoCardless</h3>
            <p className="text-sm text-gray-500">Prelevement SEPA Direct Debit</p>
          </div>
        </div>

        {config?.enabled && (
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              config.environment === 'live'
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}
          >
            {config.environment === 'live' ? 'Production' : 'Sandbox'}
          </span>
        )}
      </div>

      <div className="p-6">
        {/* Status */}
        {!isEditing && (
          <div className="mb-6">
            {config?.enabled ? (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-green-800">GoCardless est configure</p>
                  <p className="text-sm text-green-600">
                    {config.onboardingCompleted
                      ? 'Pret a accepter les prelevements SEPA'
                      : 'Configuration en cours...'}
                  </p>
                </div>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-700">GoCardless non configure</p>
                  <p className="text-sm text-gray-500">
                    Configurez votre compte pour accepter les prelevements SEPA
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Configuration Form */}
        {(isEditing || !config?.enabled) && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Environment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Environnement
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, environment: 'sandbox' })}
                  className={`
                    px-4 py-3 rounded-xl border-2 text-center transition-all
                    ${
                      formData.environment === 'sandbox'
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <p className="font-medium">Sandbox</p>
                  <p className="text-xs text-gray-500">Pour les tests</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, environment: 'live' })}
                  className={`
                    px-4 py-3 rounded-xl border-2 text-center transition-all
                    ${
                      formData.environment === 'live'
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <p className="font-medium">Production</p>
                  <p className="text-xs text-gray-500">Paiements reels</p>
                </button>
              </div>
            </div>

            {/* Access Token */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token d'acces <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={formData.accessToken}
                  onChange={(e) =>
                    setFormData({ ...formData, accessToken: e.target.value })
                  }
                  placeholder="access-token-xxxxx"
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showToken ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Disponible dans votre{' '}
                <a
                  href={
                    formData.environment === 'live'
                      ? 'https://manage.gocardless.com/developers'
                      : 'https://manage-sandbox.gocardless.com/developers'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline"
                >
                  tableau de bord GoCardless <ExternalLink className="w-3 h-3 inline" />
                </a>
              </p>
            </div>

            {/* Webhook Secret */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secret Webhook (optionnel)
              </label>
              <input
                type="password"
                value={formData.webhookSecret}
                onChange={(e) =>
                  setFormData({ ...formData, webhookSecret: e.target.value })
                }
                placeholder="whsec_xxxxx"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pour verifier les webhooks GoCardless
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-red-600">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ accessToken: '', environment: 'sandbox', webhookSecret: '' });
                    setError(null);
                  }}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
              )}

              <button
                type="submit"
                disabled={configure.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {configure.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Configuration...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Sauvegarder
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Disable button when configured */}
        {config?.enabled && !isEditing && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={handleDisable}
              disabled={disable.isPending}
              className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
            >
              {disable.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
              Desactiver GoCardless
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default GoCardlessSettings;
